import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

// Connects to data-controller="select"
export default class extends Controller {
  static targets = ["select"]
  static values = { 
    url: String,
    placeholder: String,
    turboFrame: String,
    detailsUrl: String
  }

  connect() {
    this.initializeChoices()
  }
  
  disconnect() {
    if (this.choices) {
      this.choices.destroy()
    }
  }
  
  initializeChoices() {
    const options = {
      searchEnabled: true,
      searchChoices: false, // We'll handle search via AJAX
      placeholderValue: this.placeholderValue || "Choose an option...",
      removeItemButton: true,
      shouldSort: false,
      noResultsText: "No results found",
      noChoicesText: "Start typing to search...",
      loadingText: "Loading...",
      searchResultLimit: 50,
      searchFloor: 2, // Minimum 2 characters to trigger search
    }
    
    this.choices = new Choices(this.selectTarget, options)
    
    // Set up search debouncing
    this.searchTimeout = null
    
    // Listen for search events
    this.selectTarget.addEventListener('search', (event) => {
      this.handleSearch(event.detail.value)
    })

    // Listen for change events to update the turbo frame
    this.selectTarget.addEventListener('change', (event) => {
      this.handleSelectionChange(event.target.value)
    })
  }

  handleSearch(searchTerm) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Debounce the search to avoid excessive API calls
    this.searchTimeout = setTimeout(() => {
      if (searchTerm.length >= 2) {
        this.performSearch(searchTerm)
      } else {
        // Clear choices if search term is too short
        this.choices.clearChoices()
      }
    }, 300) // Wait 300ms after user stops typing
  }

  async performSearch(searchTerm) {
    if (!this.urlValue) {
      return
    }

    try {
      // Show loading state without clearing existing choices
      this.choices.setChoices([
        {
          value: '',
          label: 'Loading...',
          disabled: true
        }
      ], 'value', 'label', false)
      
      // Build the search URL with query parameters
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('term', searchTerm)
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Transform the data for Choices.js format
      const choices = data.map(item => ({
        value: item.id,
        label: item.text,
        customProperties: {
          name: item.name,
          email: item.email,
          confirmationCode: item.confirmation_code
        }
      }))

      // Set the choices
      this.choices.setChoices(choices, 'value', 'label', true)
      
    } catch (error) {
      this.choices.setChoices([
        {
          value: '',
          label: 'Search failed. Please try again.',
          disabled: true
        }
      ], 'value', 'label', true)
    }
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  // Utility methods for external access
  getValue() {
    return this.choices ? this.choices.getValue() : null
  }

  setValue(value) {
    if (this.choices) {
      this.choices.setChoiceByValue(value)
    }
  }

  clearSelection() {
    if (this.choices) {
      this.choices.removeActiveItems()
    }
  }

  disable() {
    if (this.choices) {
      this.choices.disable()
    }
  }

  enable() {
    if (this.choices) {
      this.choices.enable()
    }
  }

  handleSelectionChange(selectedValue) {
    // If we have a turbo frame target, update the URL to include the estimate_request_id
    if (this.hasTurboFrameValue && selectedValue) {
      const url = new URL(window.location)
      url.searchParams.set('estimate_request_id', selectedValue)
      
      // Use Turbo to navigate to the new URL, which will re-render the page
      // with the estimate request details
      window.Turbo.visit(url.toString(), { frame: this.turboFrameValue })
    } else if (this.hasTurboFrameValue && selectedValue === '') {
      // Clear the estimate_request_id parameter and reload
      const url = new URL(window.location)
      url.searchParams.delete('estimate_request_id')
      window.Turbo.visit(url.toString(), { frame: this.turboFrameValue })
    }
  }
}